import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';

import { IExpenseListRow } from 'core/hooks';
import { ExpenseRecurrence, ExpenseSource } from 'core/graphql';
import { ExpenseStatusControl, ExpenseStatusCode, MoneyCell } from 'molecules';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { IExpensesSort, TSortKey } from './filtering';

const LinkButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const RecurrenceBadge = styled.span<{ recurring: boolean }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ recurring }) => (recurring ? '#ecfdf5' : '#f1f5f9')};
  color: ${({ recurring }) => (recurring ? '#047857' : '#64748b')};
`;

/** Maps the wire enum to the i18n leaf under `expenses.recurrence`. */
const RECURRENCE_I18N: Record<ExpenseRecurrence, string> = {
  [ExpenseRecurrence.None]: 'none',
  [ExpenseRecurrence.Weekly]: 'weekly',
  [ExpenseRecurrence.Monthly]: 'monthly',
};

interface IExpensesTableProps {
  rows: IExpenseListRow[];
  onRowClick: (id: string) => void;
  onSetStatus: (id: string, status: ExpenseStatusCode) => void;
  /** When omitted (e.g. group/teacher sub-tables) the headers aren't sortable. */
  sort?: IExpensesSort;
  onSort?: (key: TSortKey) => void;
}

/** Expenses register table — columns from TZ §3.5, with client-side sorting. */
export const ExpensesTable = memo(
  ({ rows, onRowClick, onSetStatus, sort, onSort }: IExpensesTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const openInvoice = useCallback(
      (e: React.MouseEvent<HTMLElement>, invoiceId: string) => {
        e.stopPropagation();
        history.push(PATHS.financesInvoice.replace(':id', invoiceId));
      },
      [history],
    );

    const sortTh = (key: TSortKey, i18nKey: string, right?: boolean) => {
      const label = t(`pages.fakturownia.expenses.columns.${i18nKey}`);
      if (!sort || !onSort) {
        return (
          <th style={{ textAlign: right ? 'right' : undefined }}>{label}</th>
        );
      }
      const active = sort.key === key;
      return (
        <th
          onClick={() => onSort(key)}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            textAlign: right ? 'right' : undefined,
            color: active ? '#0f172a' : undefined,
          }}
        >
          {label}
          <span
            style={{ marginLeft: 4, color: active ? '#2563eb' : '#cbd5e1' }}
          >
            {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </th>
      );
    };

    return (
      <S.Table>
        <thead>
          <tr>
            {sortTh('date', 'date')}
            {sortTh('amount', 'amount', true)}
            {sortTh('status', 'status')}
            <th>{t('pages.fakturownia.expenses.columns.recurrence')}</th>
            {sortTh('category', 'category')}
            {sortTh('counterparty', 'counterparty')}
            {sortTh('group', 'group')}
            <th>{t('pages.fakturownia.expenses.columns.teacher')}</th>
            <th>{t('pages.fakturownia.expenses.columns.source')}</th>
            <th>{t('pages.fakturownia.expenses.columns.document')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isFakturownia = row.source === ExpenseSource.Fakturownia;
            const isRefund = (row.amount ?? 0) < 0;
            const teacherName = row.relatedTeacher
              ? [
                  row.relatedTeacher.user?.firstName,
                  row.relatedTeacher.user?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
              : '';
            return (
              <tr
                key={row.id}
                onClick={() => onRowClick(row.id)}
                style={isRefund ? { background: '#fafafa' } : undefined}
              >
                <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  {row.date ? moment(row.date).format('DD.MM.YYYY') : '—'}
                  {row.paidAt && row.paidAt !== row.date ? (
                    <div
                      style={{ fontSize: 11, color: '#94a3b8' }}
                      title={t('pages.fakturownia.expenseForm.fields.paidAt')}
                    >
                      → {moment(row.paidAt).format('DD.MM.YYYY')}
                    </div>
                  ) : null}
                </td>
                <td>
                  <MoneyCell
                    amount={row.amount}
                    currency={row.currency}
                    negative={isRefund}
                  />
                  {isRefund && !row.isCorrection ? (
                    <div style={{ textAlign: 'right' }}>
                      <span
                        style={{
                          fontSize: 10,
                          color: '#94a3b8',
                          textTransform: 'uppercase',
                        }}
                      >
                        ↩ {t('pages.fakturownia.expenses.refundTag')}
                      </span>
                    </div>
                  ) : null}
                  {/* Correction (faktura korygująca) — shown regardless of
                      sign so a 0-amount metadata-only correction is explained
                      too. Links to the corrected (parent) invoice. */}
                  {row.isCorrection ? (
                    <div style={{ textAlign: 'right' }}>
                      <span
                        title={row.correctionReason || undefined}
                        style={{
                          fontSize: 10,
                          color: '#7c3aed',
                          textTransform: 'uppercase',
                        }}
                      >
                        ⤺{' '}
                        {row.amount === 0
                          ? t('pages.fakturownia.expenses.correctionMetaTag')
                          : t('pages.fakturownia.expenses.correctionTag')}
                      </span>
                      {row.relatedFakturowniaInvoice?.parentInvoice ? (
                        <div>
                          <button
                            type="button"
                            onClick={(e) =>
                              openInvoice(
                                e,
                                row.relatedFakturowniaInvoice!.parentInvoice!
                                  .id,
                              )
                            }
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              fontSize: 11,
                              color: '#7c3aed',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            {t('pages.fakturownia.expenses.correctionParent', {
                              number:
                                row.relatedFakturowniaInvoice.parentInvoice
                                  .number,
                            })}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {row.amountPln === null || row.amountPln === undefined ? (
                    <div style={{ textAlign: 'right' }}>
                      <span
                        title={t('pages.fakturownia.expenses.noFxRateHint')}
                        style={{ fontSize: 10, color: '#b45309' }}
                      >
                        ⚠ {t('pages.fakturownia.expenses.noFxRate')}
                      </span>
                    </div>
                  ) : null}
                </td>
                {/* Status cell is a control, not a row-open target — swallow
                    the click so it doesn't open the edit form. */}
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                <td onClick={(e) => e.stopPropagation()}>
                  <ExpenseStatusControl
                    value={row.status}
                    onChange={(s) => onSetStatus(row.id, s)}
                  />
                </td>
                <td>
                  <RecurrenceBadge
                    recurring={row.recurrence !== ExpenseRecurrence.None}
                  >
                    {t(
                      `pages.fakturownia.expenses.recurrence.${
                        RECURRENCE_I18N[row.recurrence] ?? 'none'
                      }`,
                    )}
                  </RecurrenceBadge>
                </td>
                <td>{row.category?.name || '—'}</td>
                <td>{row.counterpartyName || '—'}</td>
                <td>
                  {row.relatedGroup ? (
                    <LinkButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(
                          PATHS.groupDetail.replace(
                            ':id',
                            row.relatedGroup!.id,
                          ),
                        );
                      }}
                    >
                      {row.relatedGroup.name}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {row.relatedTeacher ? (
                    <LinkButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(
                          PATHS.teacherDetail.replace(
                            ':id',
                            row.relatedTeacher!.id,
                          ),
                        );
                      }}
                    >
                      {teacherName || '—'}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ fontSize: 11, color: '#64748b' }}>
                  {isFakturownia ? 'Fakturownia' : 'Manual'}
                </td>
                <td>
                  {row.documentUrl ? (
                    <a
                      href={row.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2563eb', fontSize: 12 }}
                    >
                      📎 {t('pages.fakturownia.expenses.openDocument')}
                    </a>
                  ) : row.relatedFakturowniaInvoice ? (
                    <LinkButton
                      type="button"
                      onClick={(e) =>
                        openInvoice(e, row.relatedFakturowniaInvoice!.id)
                      }
                    >
                      {t('pages.fakturownia.expenses.openInvoice', {
                        number: row.relatedFakturowniaInvoice.number,
                      })}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </S.Table>
    );
  },
);
